/**
 * @file   src\mobile\pages\WantAdListing.js
 * @brief  This file is responsible for my want ad listing
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { Tabs, Tab } from "react-bootstrap";
import "../../styles/clients.scss";
import MenuBottomBar from "../../components/v2/menuBottomMobile";
import PublishedWantAds from "../../components/WantAdListing/PublishedWantAds";
import ClosedWantAds from "../../components/WantAdListing/ClosedWantAds";
import DraftWantAds from "../../components/WantAdListing/DraftWantAds";
import {
  checkTutorialDisplayStatus,
  classesBasedOnScroll,
  getUserId,
  getUserType,
  saveTutorialStatusToLocal,
} from "../../../helpers/common";
import {
  PAUSED_TAB,
  DRAFT_TAB,
  PUBLISHED_TAB,
  USER_TYPE_AGENT,
  SCROLL_DIRECTION_DOWN,
} from "../../../constants/common";
import { getMyWantAds } from "../../../services/v2/wantAdService";
import ClientListing from "../../components/WantAdListing/ClientListing";
import { getClientDetails } from "../../../services/agentClientsService";
import Sortoffcanvas from "../../components/common/Sortoffcanvas";
import { listContext } from "../../../contextApi/wantAdListContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { saveTutorialStatus } from "../../../services/commonServices";
import { pageURLs } from "../../../constants/pageURLs";
import HandleScroll from "../../commonComponents/HandleScroll";
import { useSelector } from "react-redux";

const WantAdListing = () => {
  const scrollStates = HandleScroll();
  const isReachedBottom = _.get(scrollStates, "isReachedBottom", null);
  const scrollDirection = _.get(scrollStates, "direction", null);
  const navigate = useNavigate();
  const location = useLocation();
  const pageTopRef = useRef(null);
  const PAGE_LIMIT = 10;
  const INITIAL_PAGE_INDEX = 1;
  const tutorialPage = 9;
  const userType = getUserType();
  const showDraft =
    location && location.state ? location.state["showDraft"] : false;
  const [showDraftTutorial, setShowDraftTutorial] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(null);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails
  );

  // To set the tab based on the value from URL mainly for link from Email
  const [searchParams] = useSearchParams();
  searchParams.get("Tab");
  const TabValueFromURL = searchParams.get("Tab");

  const initialListStates = {
    activeTab: TabValueFromURL
      ? TabValueFromURL
      : showDraft
      ? DRAFT_TAB
      : PUBLISHED_TAB,
    currPage: INITIAL_PAGE_INDEX,
    list: null,
    clientId: null,
  };
  const [showSort, setShowSort] = useState(false);
  const sortClose = () => setShowSort(false);
  const sortShow = () => setShowSort(true);
  const [listStates, setListStates] = useState(initialListStates);
  const options = [
    {
      value: "desc",
      label: "Newest",
    },
    { value: "asc", label: "Oldest" },
  ];
  const [sortParam, setSortParam] = useState(options[0]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("Tab")) {
      setListStates((prevState) => ({
        ...prevState,
        activeTab: params.get("Tab"),
        currPage: INITIAL_PAGE_INDEX,
        list: null,
        clientId: "",
      }));
    } else {
      params.set("Tab", "Published");
      window.history.replaceState(
        {},
        "",
        `${location.pathname}?${params.toString()}`
      );
    }
  }, [location.search]);
  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
      } else {
        setIsPremiumUser(false);
      }
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    window.scrollTo(0, pageTopRef.current.offsetTop);
    document.body.classList.add("bg-offcanvas-fix");
  }, []);

  useEffect(() => {
    if (isReachedBottom) {
      setListStates((prevState) => ({
        ...prevState,
        currPage: prevState.currPage + 1,
      }));
    }
  }, [isReachedBottom]);

  useEffect(() => {
    const clientId = listStates?.clientId;
    const reqPayload = {
      page: listStates.currPage,
      limit: PAGE_LIMIT,
      status: listStates.activeTab,
      sortBy: "createdon",
      sortType: sortParam?.value,
    };

    if (clientId) {
      reqPayload.clientId = clientId;
      reqPayload.isClientInfoReq = false;
      reqPayload.type = 1;
      reqPayload.sortBy = "publishedon";
      getClientWantAds(reqPayload);
    } else {
      reqPayload.userId = getUserId();
      getWantAdList(reqPayload);
    }
  }, [
    listStates.activeTab,
    listStates.currPage,
    sortParam,
    listStates.clientId,
  ]);

  useEffect(() => {
    //Need to show inventory onboard very first time
    if (listStates?.list?.length > 0 && listStates?.activeTab == DRAFT_TAB) {
      let DisplayInventoryAddOnboard = checkTutorialDisplayStatus(tutorialPage); //Inventory onboard display
      if (DisplayInventoryAddOnboard) {
        setShowDraftTutorial(true);
      }
    }
  }, [listStates]);

  const handleTutorialClose = () => {
    saveTutorialStatusToLocal(tutorialPage);
    saveTutorialToDb();
    setShowDraftTutorial(false);
  };

  const saveTutorialToDb = async () => {
    await saveTutorialStatus(tutorialPage, 1); //Save status
  };
  /**
   * To get want ad list
   * @param {*} reqPayload
   */
  const getWantAdList = async (reqPayload) => {
    await getMyWantAds(reqPayload)
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const list = _.get(response, "result", null);
          const currPage = reqPayload.page;
          if (currPage === INITIAL_PAGE_INDEX) {
            setListStates((prevState) => ({
              ...prevState,
              list: list,
            }));
          } else {
            setListStates((prevState) => ({
              ...prevState,
              list: [...prevState.list, ...list],
            }));
          }
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };
  /**
   * To get client want ads
   * @param {*} reqPayload
   */
  const getClientWantAds = async (reqPayload) => {
    await getClientDetails(reqPayload)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          if (reqPayload.page === INITIAL_PAGE_INDEX) {
            setListStates((prevState) => ({
              ...prevState,
              list: _.get(responseResult, "wantAdList", []),
            }));
          } else {
            setListStates((prevState) => ({
              ...prevState,
              list: [
                ...prevState.list,
                ..._.get(responseResult, "wantAdList", []),
              ],
            }));
          }
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const handleChangeClient = (e) => {
    const clientId = e?.value || null;
    setListStates((prevState) => ({
      ...prevState,
      clientId: clientId,
      currPage: INITIAL_PAGE_INDEX,
      list: null,
    }));
  };

  const handleSortSelection = (selectedItem) => {
    setSortParam(selectedItem);
    sortClose();
    setListStates((prevState) => ({
      ...prevState,
      currPage: INITIAL_PAGE_INDEX,
      list: null,
    }));
  };

  const updateStateAfterDelete = async (wantAdId) => {
    if (wantAdId) {
      const currentAdList = [..._.get(listStates, "list", [])];

      const arrayAfterDelete = currentAdList.filter(function (ca) {
        return ca.Want_ad_Id !== wantAdId;
      });
      setListStates((prevState) => ({
        ...prevState,
        list: arrayAfterDelete,
      }));
    }
  };

  const { mainWrapperClass, contentWrapperClass } =
    classesBasedOnScroll(scrollDirection);
  return (
    <div className={mainWrapperClass} ref={pageTopRef}>
      <div className={contentWrapperClass}>
        <div className="inner-layout-header-new bg-white">
          <div className="top-three-comp-header w-100 top-three-comp-headerborder">
            <div className="top-three-comp-header-inner w-100">
              <a
                className="back-link-btn"
                href={void 0}
                role="button"
                onClick={() => navigate(pageURLs.proDashboard)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.1003 5.39972C10.3932 5.69262 10.3932 6.16749 10.1003 6.46038L5.31066 11.2501H20.5C20.9142 11.2501 21.25 11.5858 21.25 12.0001C21.25 12.4143 20.9142 12.7501 20.5 12.7501H5.31066L10.1003 17.5397C10.3932 17.8326 10.3932 18.3075 10.1003 18.6004C9.80744 18.8933 9.33256 18.8933 9.03967 18.6004L2.96967 12.5304C2.82902 12.3897 2.75 12.199 2.75 12.0001C2.75 11.8011 2.82902 11.6104 2.96967 11.4697L9.03967 5.39972C9.33256 5.10683 9.80744 5.10683 10.1003 5.39972Z"
                    fill="#1B1C1E"
                  />
                </svg>
              </a>
              <div className="heading">My Want-Listings</div>
              <div className="right-mobbtn-wrap">
                <a
                  className="action-btn"
                  href={void 0}
                  role="button"
                  onClick={() => sortShow()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10 10C10 10.1658 9.93415 10.3247 9.81694 10.4419C9.69973 10.5592 9.54076 10.625 9.375 10.625H3.75C3.58424 10.625 3.42527 10.5592 3.30806 10.4419C3.19085 10.3247 3.125 10.1658 3.125 10C3.125 9.83424 3.19085 9.67527 3.30806 9.55806C3.42527 9.44085 3.58424 9.375 3.75 9.375H9.375C9.54076 9.375 9.69973 9.44085 9.81694 9.55806C9.93415 9.67527 10 9.83424 10 10ZM3.75 5.625H14.375C14.5408 5.625 14.6997 5.55915 14.8169 5.44194C14.9342 5.32473 15 5.16576 15 5C15 4.83424 14.9342 4.67527 14.8169 4.55806C14.6997 4.44085 14.5408 4.375 14.375 4.375H3.75C3.58424 4.375 3.42527 4.44085 3.30806 4.55806C3.19085 4.67527 3.125 4.83424 3.125 5C3.125 5.16576 3.19085 5.32473 3.30806 5.44194C3.42527 5.55915 3.58424 5.625 3.75 5.625ZM8.125 14.375H3.75C3.58424 14.375 3.42527 14.4408 3.30806 14.5581C3.19085 14.6753 3.125 14.8342 3.125 15C3.125 15.1658 3.19085 15.3247 3.30806 15.4419C3.42527 15.5592 3.58424 15.625 3.75 15.625H8.125C8.29076 15.625 8.44973 15.5592 8.56694 15.4419C8.68415 15.3247 8.75 15.1658 8.75 15C8.75 14.8342 8.68415 14.6753 8.56694 14.5581C8.44973 14.4408 8.29076 14.375 8.125 14.375ZM17.9422 12.6828C17.8841 12.6247 17.8152 12.5786 17.7393 12.5472C17.6635 12.5157 17.5821 12.4995 17.5 12.4995C17.4179 12.4995 17.3365 12.5157 17.2607 12.5472C17.1848 12.5786 17.1159 12.6247 17.0578 12.6828L15 14.7414V8.75C15 8.58424 14.9342 8.42527 14.8169 8.30806C14.6997 8.19085 14.5408 8.125 14.375 8.125C14.2092 8.125 14.0503 8.19085 13.9331 8.30806C13.8158 8.42527 13.75 8.58424 13.75 8.75V14.7414L11.6922 12.6828C11.5749 12.5655 11.4159 12.4997 11.25 12.4997C11.0841 12.4997 10.9251 12.5655 10.8078 12.6828C10.6905 12.8001 10.6247 12.9591 10.6247 13.125C10.6247 13.2909 10.6905 13.4499 10.8078 13.5672L13.9328 16.6922C13.9909 16.7503 14.0598 16.7964 14.1357 16.8279C14.2115 16.8593 14.2929 16.8755 14.375 16.8755C14.4571 16.8755 14.5385 16.8593 14.6143 16.8279C14.6902 16.7964 14.7591 16.7503 14.8172 16.6922L17.9422 13.5672C18.0003 13.5091 18.0464 13.4402 18.0779 13.3643C18.1093 13.2885 18.1255 13.2071 18.1255 13.125C18.1255 13.0429 18.1093 12.9615 18.0779 12.8857C18.0464 12.8098 18.0003 12.7409 17.9422 12.6828Z"
                      fill="#1B1C1E"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="home-layout-content">
          <div className="tab-wrap tab-wrap-fix">
            {userType === USER_TYPE_AGENT && isPremiumUser && (
              <div className="search_wrap_mobile_top input-group">
                <ClientListing
                  handleChangeClient={handleChangeClient}
                  clientId={_.get(listStates, "clientId", null)}
                />
              </div>
            )}

            <listContext.Provider
              value={{
                list: _.get(listStates, "list", null),
                status: _.get(listStates, "activeTab", []),
                updateStateAfterDelete,
              }}
            >
              <div className="tab-scroll-hide"></div>
              <Tabs
                id="controlled-tab-example"
                defaultActiveKey={PUBLISHED_TAB}
                activeKey={_.get(listStates, "activeTab", PUBLISHED_TAB)}
                onSelect={(tab) => {
                  const params = new URLSearchParams(window.location.search);
                  params.set("Tab", tab);
                  const newUrl = `${
                    window.location.pathname
                  }?${params.toString()}`;
                  window.history.pushState({}, "", newUrl);

                  setListStates((prevState) => ({
                    ...prevState,
                    activeTab: tab,
                    currPage: INITIAL_PAGE_INDEX,
                    list: null,
                    clientId: "",
                  }));
                }}
              >
                <Tab eventKey={PUBLISHED_TAB} title={PUBLISHED_TAB}>
                  <PublishedWantAds />
                </Tab>
                <Tab eventKey={PAUSED_TAB} title={PAUSED_TAB}>
                  <ClosedWantAds />
                </Tab>

                <Tab eventKey={DRAFT_TAB} title={DRAFT_TAB}>
                  <>
                    {showDraftTutorial && (
                      <div className="draft-tutorial-info position-relative">
                        <button
                          className="btn strengthen-ad-closebtn"
                          onClick={() => handleTutorialClose()}
                        >
                          close
                        </button>
                        <span>
                          Your want-listing has been saved to your drafts.
                        </span>{" "}
                        When you’re ready to continue building it, you can find
                        it here, under the{" "}
                        {isPremiumUser ? "Pro Dashboard" : "My Portfolio"} tab.
                      </div>
                    )}
                    <DraftWantAds />
                  </>
                </Tab>
              </Tabs>
            </listContext.Provider>
          </div>
        </div>
      </div>

      {scrollDirection !== SCROLL_DIRECTION_DOWN && (
        <div className="inner-layout-footer-new inner-layout-footer-topfix">
          <div className="bottomMenu bottom-menu-index">
            <MenuBottomBar scrollDirection={scrollDirection} />
          </div>
        </div>
      )}

      <Sortoffcanvas
        show={showSort}
        onHide={sortClose}
        handleSortParam={handleSortSelection}
        options={options}
        sortParam={sortParam}
      />
    </div>
  );
};

export default WantAdListing;
